import { ToastId, useToast, UseToastOptions } from '@chakra-ui/react';
import { useCallback, useRef } from 'react';
import { delay } from '../delay';

export default function useSnacks() {
  const toast = useToast();
  const lastToastRef = useRef<(UseToastOptions & { id: ToastId }) | null>(null);

  const showToast = useCallback(
    async (options: UseToastOptions) => {
      // avoid duplicates
      if (lastToastRef.current) {
        const { description, status, title } = lastToastRef.current;
        if (
          description === options.description &&
          status === options.status &&
          title === options.title
        ) {
          toast.close(lastToastRef.current.id);
        }
      }

      await delay(300);

      const toastId = toast(options);
      lastToastRef.current = { ...options, id: toastId as ToastId };
    },
    [toast],
  );

  const showError = useCallback(
    (msg = 'Si è verificato un problema, riprova più tardi') => {
      showToast({ description: msg, status: 'error', position: 'top' });
    },
    [showToast],
  );

  const showSuccess = useCallback(
    (msg = 'Operazione effettuata con successo') => {
      showToast({ description: msg, status: 'success', position: 'top' });
    },
    [showToast],
  );

  const showWarning = useCallback(
    (msg: string, duration?: number) => {
      showToast({ description: msg, status: 'warning', duration, position: 'top' });
    },
    [showToast],
  );

  const showSnack = useCallback(
    (msg: string, duration?: number) => {
      showToast({ description: msg, duration, position: 'top' });
    },
    [showToast],
  );

  const closeSnackbar = useCallback(() => toast.closeAll(), [toast]);

  return {
    showError,
    showSuccess,
    showWarning,
    showSnack,
    closeSnackbar,
  };
}
